import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import {
  resolveReadmeImageUrl,
  resolveReadmeLinkUrl,
} from "../../utils/readmeAssetUrl";
import github from "../../context/github/githubApi";
import Spinner from "../layout/spinner";
import NotFound from "./NotFound";
import { compactNumber } from "../../utils/relativeTime";
import { IconGitHub } from "../users/profileIcons";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
};

const LANG_DOT = {
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  CSS: "#563d7c",
  HTML: "#e34c26",
  Python: "#3572A5",
  Ruby: "#701516",
  Go: "#00add8",
  Rust: "#dea584",
  C: "#555555",
  "C++": "#f34b7d",
  "C#": "#178600",
  Shell: "#89e051",
  PHP: "#4f5d95",
  Java: "#b07219",
  Swift: "#f05138",
  Kotlin: "#a97bff",
  Vue: "#41b883",
};

const langDotColor = (lang) => (lang && LANG_DOT[lang]) || "#8b949e";

const IcoCode = () => (
  <svg className="gf-repo-detail__svg-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M16 18l6-6-6-6M8 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IcoBranch = () => (
  <svg className="gf-repo-detail__svg-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    <circle cx="18" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    <circle cx="18" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8.5 7.5c2 2.5 5 2.5 7.5 0M8.5 7.5v6a4 4 0 004 4h1.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IcoCalendar = () => (
  <svg className="gf-repo-detail__svg-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const IcoClock = () => (
  <svg className="gf-repo-detail__svg-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IcoIssue = () => (
  <svg className="gf-repo-detail__svg-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const readmeMdComponents = {
  a: ({ href, children, ...rest }) => {
    const external = href && /^https?:\/\//i.test(href);
    return (
      <a
        {...rest}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    );
  },
};

const RepoDetail = () => {
  const { owner, name } = useParams();
  const [repo, setRepo] = useState(null);
  const [topics, setTopics] = useState([]);
  const [readme, setReadme] = useState(null);
  const [readmeFilePath, setReadmeFilePath] = useState("README.md");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [ownerUser, setOwnerUser] = useState(null);
  const [otherRepos, setOtherRepos] = useState([]);
  const [asideOpen, setAsideOpen] = useState(false);

  useEffect(() => {
    setAsideOpen(false);
  }, [owner, name]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 992px)");
    const close = () => {
      if (mq.matches) setAsideOpen(false);
    };
    mq.addEventListener("change", close);
    close();
    return () => mq.removeEventListener("change", close);
  }, []);

  useEffect(() => {
    if (!asideOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAsideOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [asideOpen]);

  useEffect(() => {
    if (!asideOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [asideOpen]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setErr(null);
      setRepo(null);
      setReadme(null);
      setReadmeFilePath("README.md");
      setTopics([]);
      setOwnerUser(null);
      setOtherRepos([]);
      try {
        const res = await github.get(
          `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`
        );
        if (cancelled) return;
        setRepo(res.data);
        if (Array.isArray(res.data.topics) && res.data.topics.length) {
          setTopics(res.data.topics);
        } else {
          try {
            const tr = await github.get(
              `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/topics`,
              {
                headers: {
                  Accept: "application/vnd.github.mercy-preview+json",
                },
              }
            );
            if (!cancelled && tr.data?.names) setTopics(tr.data.names);
          } catch {
            /* optional */
          }
        }
        try {
          const rm = await github.get(
            `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/readme`,
            { headers: { Accept: "application/vnd.github+json" } }
          );
          if (cancelled || !rm.data?.content) return;
          const raw = atob(rm.data.content.replace(/\n/g, ""));
          setReadmeFilePath(
            typeof rm.data.path === "string" && rm.data.path
              ? rm.data.path
              : "README.md"
          );
          setReadme(raw.length > 400000 ? `${raw.slice(0, 400000)}\n\n…` : raw);
        } catch {
          setReadme(null);
          setReadmeFilePath("README.md");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(
            e.response?.status === 404
              ? "Repository not found."
              : "Could not load repository."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (owner && name) load();
    return () => {
      cancelled = true;
    };
  }, [owner, name]);

  useEffect(() => {
    if (!repo) return;
    let cancelled = false;
    const login = repo.owner?.login;
    if (!login) return;

    github
      .get(`/users/${encodeURIComponent(login)}`)
      .then((res) => {
        if (!cancelled) setOwnerUser(res.data);
      })
      .catch(() => {
        if (!cancelled) setOwnerUser(null);
      });

    github
      .get(`/users/${encodeURIComponent(login)}/repos`, {
        params: { per_page: 10, sort: "pushed", direction: "desc" },
      })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data || []).filter((r) => r.name !== name).slice(0, 6);
        setOtherRepos(list);
      })
      .catch(() => {
        if (!cancelled) setOtherRepos([]);
      });

    return () => {
      cancelled = true;
    };
  }, [repo, name]);

  useEffect(() => {
    document.title = repo
      ? `${repo.full_name} · GitHub Finder`
      : "Repository · GitHub Finder";
  }, [repo]);

  const readmeUrlTransform = useMemo(() => {
    if (!readme || !repo) return defaultUrlTransform;
    const branch = repo.default_branch || "main";
    return (url, key) => {
      let resolved = url;
      if (key === "src") {
        resolved = resolveReadmeImageUrl(
          owner,
          name,
          branch,
          readmeFilePath,
          url
        );
      } else if (key === "href") {
        resolved = resolveReadmeLinkUrl(
          owner,
          name,
          branch,
          readmeFilePath,
          url
        );
      }
      return defaultUrlTransform(resolved);
    };
  }, [readme, repo, owner, name, readmeFilePath]);

  if (loading) {
    return (
      <div className="gf-user-page">
        <Spinner />
      </div>
    );
  }

  if (err || !repo) {
    const is404 = err === "Repository not found.";
    return (
      <NotFound
        code={is404 ? "404" : "—"}
        title={is404 ? "Repository not found" : "Couldn’t load repository"}
        lead={
          is404
            ? `There is no public repository at ${owner}/${name}. Check the owner and repo name, or it may be private or deleted.`
            : err || "Something went wrong while loading this repository. Try again in a moment."
        }
        documentTitle={
          is404
            ? `Repository not found · ${owner}/${name} · GitHub Finder`
            : `Error · ${owner}/${name} · GitHub Finder`
        }
      />
    );
  }

  const ownerLogin = repo.owner?.login ?? owner;
  const ownerAvatar = ownerUser?.avatar_url || repo.owner?.avatar_url;
  const ownerHtml = ownerUser?.html_url || repo.owner?.html_url;
  const displayName = ownerUser?.name?.trim() || ownerLogin;
  const followers = ownerUser?.followers;
  const following = ownerUser?.following;
  const publicRepos = ownerUser?.public_repos;
  const publicGists = ownerUser?.public_gists ?? 0;
  const followHref = ownerHtml || `https://github.com/${encodeURIComponent(ownerLogin)}`;

  return (
    <div className="gf-user-page gf-repo-detail gf-user-dashboard">
      <div className="gf-user-dashboard__split gf-user-dashboard__split--profile">
        {asideOpen && (
          <button
            type="button"
            className="gf-user-profile-sidebar-backdrop"
            aria-label="Close owner panel"
            onClick={() => setAsideOpen(false)}
          />
        )}
        <aside
          id="gf-repo-detail-sidebar"
          className={`gf-user-profile-sidebar gf-repo-detail-aside${asideOpen ? " is-open" : ""}`}
          aria-label="Owner and related repositories"
        >
          <div className="gf-user-profile-sidebar__mobile-bar">
            <span className="gf-user-profile-sidebar__mobile-title">Owner</span>
            <button
              type="button"
              className="gf-user-profile-sidebar__close"
              onClick={() => setAsideOpen(false)}
              aria-label="Close panel"
            >
              ✕
            </button>
          </div>

          <div className="gf-repo-detail-aside__inner">
            <section className="gf-repo-detail-aside__profile">
              <div className="gf-repo-detail-aside__header">
                <img
                  src={ownerAvatar}
                  alt=""
                  className="gf-repo-detail-aside__avatar"
                  width={112}
                  height={112}
                />
                <div className="gf-repo-detail-aside__name-row">
                  <h2 className="gf-repo-detail-aside__name">{displayName}</h2>
                  <span className="gf-repo-detail-aside__verified" title="GitHub user" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="var(--gf-primary)"
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                      />
                    </svg>
                  </span>
                </div>
                <p className="gf-repo-detail-aside__handle">@{ownerLogin}</p>
              </div>

              <Link
                to={`/user/${encodeURIComponent(ownerLogin)}`}
                className="gf-repo-detail-aside__btn gf-repo-detail-aside__btn--follow"
              >
                View Profile
              </Link>

              <div className="gf-repo-detail-aside__stat-bar" role="group" aria-label="Owner statistics">
                <div className="gf-repo-detail-aside__stat-cell">
                  <span className="gf-repo-detail-aside__stat-ico" aria-hidden="true">
                    ⭐
                  </span>
                  <span className="gf-repo-detail-aside__stat-val">{compactNumber(followers ?? 0)}</span>
                </div>
                <div className="gf-repo-detail-aside__stat-div" aria-hidden="true" />
                <div className="gf-repo-detail-aside__stat-cell gf-repo-detail-aside__stat-cell--mid">
                  <span className="gf-repo-detail-aside__stat-val">
                    {following ?? 0}:{publicGists}
                  </span>
                </div>
                <div className="gf-repo-detail-aside__stat-div" aria-hidden="true" />
                <div className="gf-repo-detail-aside__stat-cell">
                  <span className="gf-repo-detail-aside__stat-ico" aria-hidden="true">
                    🍴
                  </span>
                  <span className="gf-repo-detail-aside__stat-val">{publicRepos ?? 0}</span>
                </div>
              </div>

              <a
                href={followHref}
                className="gf-repo-detail-aside__btn gf-repo-detail-aside__btn--github"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="gf-repo-detail-aside__btn-github-inner">
                  <IconGitHub />
                  Open on GitHub
                </span>
                <span className="gf-repo-detail-aside__btn-github-ext" aria-hidden="true">
                  ↗
                </span>
              </a>
            </section>

            <section className="gf-repo-detail-aside__repos" aria-label="Other repositories">
              <h2 className="gf-repo-detail-aside__repos-title">Other Repositories</h2>
              {otherRepos.length === 0 ? (
                <p className="gf-repo-detail-aside__repos-empty gf-muted">No other public repositories in this sample.</p>
              ) : (
                <ul className="gf-repo-detail-aside__repo-list">
                  {otherRepos.map((r) => (
                    <li key={r.id} className="gf-repo-detail-aside__repo-item">
                      <Link to={`/repo/${r.owner.login}/${r.name}`} className="gf-repo-detail-aside__repo-link">
                        <div className="gf-repo-detail-aside__repo-top">
                          <span className="gf-repo-detail-aside__repo-ico" aria-hidden="true">
                            📦
                          </span>
                          <span className="gf-repo-detail-aside__repo-title">{r.name}</span>
                          {r.language && (
                            <span className="gf-repo-detail-aside__repo-lang-badge">{r.language}</span>
                          )}
                        </div>
                        <div className="gf-repo-detail-aside__repo-sub">
                          <span>{r.language || "—"}</span>
                          <span className="gf-repo-detail-aside__repo-dot">·</span>
                          <span>{compactNumber(r.stargazers_count ?? 0)}</span>
                          <span aria-hidden="true">⭐</span>
                          <span>{compactNumber(r.forks_count ?? 0)}</span>
                          <span aria-hidden="true">🍴</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={`/user/${encodeURIComponent(ownerLogin)}`}
                className="gf-repo-detail-aside__view-all"
              >
                <span>View all</span>
                <span className="gf-repo-detail-aside__view-all-caret" aria-hidden="true">
                  ▾
                </span>
              </Link>
            </section>
          </div>
        </aside>

        <div className="gf-user-profile-main gf-repo-detail__main">
          <header className="gf-repo-detail__hero gf-card">
            <div className="gf-repo-detail__hero-top">
              <div className="gf-repo-detail__hero-head">
                <h1 className="gf-repo-detail__title">{repo.name}</h1>
                <p className="gf-repo-detail__owner-line gf-muted">
                  <Link to={`/user/${encodeURIComponent(ownerLogin)}`} className="gf-repo-detail__owner-link">
                    @{ownerLogin}
                  </Link>
                  {repo.full_name && (
                    <span className="gf-repo-detail__full-name"> · {repo.full_name}</span>
                  )}
                </p>
              </div>
              <a
                href={repo.html_url}
                className="gf-repo-detail__gh-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="gf-repo-detail__gh-btn-inner">
                  <IconGitHub />
                  Open repository on GitHub
                </span>
                <span className="gf-repo-detail__gh-btn-ext" aria-hidden="true">
                  ↗
                </span>
              </a>
            </div>

            <div className="gf-repo-detail__meta-bar">
              {repo.language && (
                <span className="gf-repo-detail__lang-pill">{repo.language}</span>
              )}
              <div className="gf-repo-detail__meta-stats" aria-label="Repository statistics">
                <span>⭐ {repo.stargazers_count?.toLocaleString?.() ?? 0} stars</span>
                <span className="gf-repo-detail__pipe" aria-hidden="true">
                  |
                </span>
                <span>🍴 {repo.forks_count?.toLocaleString?.() ?? 0} forks</span>
                <span className="gf-repo-detail__pipe" aria-hidden="true">
                  |
                </span>
                <span>
                  👁{" "}
                  {(repo.subscribers_count ?? repo.watchers_count ?? repo.watchers ?? 0).toLocaleString?.() ??
                    0}{" "}
                  watchers
                </span>
              </div>
              <span className="gf-repo-detail__meta-updated">
                Last updated {fmtDate(repo.updated_at)}
              </span>
            </div>
          </header>

          {repo.description && (
            <section className="gf-card gf-repo-detail__block gf-repo-detail__desc-block">
              <h2 className="gf-repo-detail__block-title">Description</h2>
              <p className="gf-repo-detail__desc-text">{repo.description}</p>
            </section>
          )}

          <section className="gf-card gf-repo-detail__block gf-repo-detail__details-block">
            <h2 className="gf-repo-detail__block-title">Repository details</h2>
            <div className="gf-repo-detail__detail-grid">
              <div className="gf-repo-detail__detail-cell">
                <div className="gf-repo-detail__detail-head">
                  <IcoCode />
                  <span className="gf-repo-detail__detail-label">Language</span>
                </div>
                <div className="gf-repo-detail__detail-value">
                  {repo.language ? (
                    <>
                      <span
                        className="gf-repo-detail__lang-dot"
                        style={{ background: langDotColor(repo.language) }}
                        aria-hidden="true"
                      />
                      {repo.language}
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="gf-repo-detail__detail-cell">
                <div className="gf-repo-detail__detail-head">
                  <IcoBranch />
                  <span className="gf-repo-detail__detail-label">Default branch</span>
                </div>
                <div className="gf-repo-detail__detail-value">
                  {repo.default_branch || "—"}
                </div>
              </div>
              <div className="gf-repo-detail__detail-cell">
                <div className="gf-repo-detail__detail-head">
                  <IcoCalendar />
                  <span className="gf-repo-detail__detail-label">Created</span>
                </div>
                <div className="gf-repo-detail__detail-value">{fmtDate(repo.created_at)}</div>
              </div>
              <div className="gf-repo-detail__detail-cell">
                <div className="gf-repo-detail__detail-head">
                  <IcoClock />
                  <span className="gf-repo-detail__detail-label">Last updated</span>
                </div>
                <div className="gf-repo-detail__detail-value">{fmtDate(repo.updated_at)}</div>
              </div>
              <div className="gf-repo-detail__detail-cell gf-repo-detail__detail-cell--full">
                <div className="gf-repo-detail__detail-head">
                  <IcoIssue />
                  <span className="gf-repo-detail__detail-label">Open issues</span>
                </div>
                <div className="gf-repo-detail__detail-value">
                  {repo.open_issues_count ?? "—"}
                </div>
              </div>
            </div>
            {topics.length > 0 && (
              <div className="gf-repo-detail__topics">
                <h3 className="gf-repo-detail__h3">Topics</h3>
                <div className="gf-badges">
                  {topics.map((t) => (
                    <span key={t} className="gf-badge">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {readme && (
            <section className="gf-card gf-repo-detail__block gf-repo-detail__readme-section">
              <h2 className="gf-repo-detail__block-title">README preview</h2>
              <div className="gf-repo-detail__readme">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  urlTransform={readmeUrlTransform}
                  components={readmeMdComponents}
                >
                  {readme}
                </ReactMarkdown>
              </div>
            </section>
          )}
        </div>
      </div>

      <button
        type="button"
        className={`gf-user-profile-fab${asideOpen ? " is-hidden" : ""}`}
        onClick={() => setAsideOpen(true)}
        aria-controls="gf-repo-detail-sidebar"
        aria-label="Open owner and related repositories"
      >
        <span className="gf-user-profile-fab__ico" aria-hidden="true">
          ☰
        </span>
        <span className="gf-user-profile-fab__txt">Owner</span>
      </button>
    </div>
  );
};

export default RepoDetail;
